import type { FactorInputs, FactorName, PipelineResult } from './dip-pipeline.types'
import type { AsymmetricWeight, FactorWeights } from './dip-factors.schema'

const FACTOR_NAMES: FactorName[] = [
  'disproportion',
  'avgCostDistance',
  'freeCash',
  'accountCash',
  'lotPrice',
  'mood',
  'desire',
]

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))
const clampAbs = (v: number, limit: number) => clamp(v, -limit, limit)
const isAsymmetric = (w: number | AsymmetricWeight): w is AsymmetricWeight =>
  typeof w === 'object' && w !== null

function applyRate(x: number, w: number | AsymmetricWeight): number {
  if (isAsymmetric(w)) return x >= 0 ? x * w.positive : x * w.negative
  return x * w
}

// mood/desire are never "missing": their dial defaults to 0, so input is always a number.
function rawInput(name: FactorName, inputs: FactorInputs): number | null {
  switch (name) {
    case 'mood':
      return inputs.mood
    case 'desire':
      return inputs.desire
    default:
      return inputs[name]
  }
}

export function computeEffectivePercent(
  inputs: FactorInputs,
  weights: FactorWeights,
  bounds: { min: number; max: number },
  leg: 'drop' | 'shift'
): PipelineResult {
  const contributions = {
    disproportion: 0,
    avgCostDistance: 0,
    freeCash: 0,
    accountCash: 0,
    lotPrice: 0,
    mood: 0,
    desire: 0,
  } as Record<FactorName, number>

  let total = bounds.min // floor / start value

  for (const name of FACTOR_NAMES) {
    const factor = weights[name]
    if (!factor.enabled) continue
    const raw = rawInput(name, inputs)
    if (raw === null) continue // inert when no input available

    let x = raw
    if (factor.pivot != null) x = factor.pivot !== 0 ? (x - factor.pivot) / factor.pivot : 0
    if (factor.limit != null) x = clampAbs(x, factor.limit)

    const weight = leg === 'drop' ? factor.dropWeight : factor.shiftWeight
    const contribution = applyRate(x, weight)
    contributions[name] = contribution
    total += contribution
  }

  return { value: clamp(total, bounds.min, bounds.max), contributions }
}
